import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';

/**
 * LocationContext — single source of truth for which location the dashboard
 * is currently filtering on.
 *
 * `activeLocationId === 'all'` means "Aggregate all locations" (the default
 * when a multi-location business loads). Single-location businesses always
 * see their primary location's id.
 *
 * Exposes:
 *   - locations            list of {id, name, twilio_number, is_primary, ...}
 *   - activeLocationId     currently selected ('all' | location id)
 *   - setActiveLocationId  setter (persists to localStorage per business)
 *   - maxLocations         cap from the user's plan
 *   - canAddLocation       boolean
 *   - refresh()            re-fetch the locations list (after add / edit)
 *   - apiQueryParam()      append ?location_id=X if we're filtering, else ''
 */
const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const { user, api } = useAuth();
  const [locations, setLocations] = useState([]);
  const [primaryId, setPrimaryId] = useState(null);
  const [maxLocations, setMaxLocations] = useState(1);
  const [activeLocationId, _setActiveLocationId] = useState('all');
  const [loading, setLoading] = useState(true);

  const businessId = user?.business?.id || null;

  const fetchLocations = useCallback(async () => {
    if (!businessId) {
      setLocations([]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/locations');
      setLocations(data?.locations || []);
      setPrimaryId(data?.primary_location_id || null);
      setMaxLocations(data?.max_locations || 1);
    } catch (e) {
      // Single-location businesses still get a 1-element array via the
      // backend's lazy migration. A 404/500 here means the user isn't
      // attached to a business yet — silently fall back to empty.
      setLocations([]);
      setMaxLocations(1);
    } finally {
      setLoading(false);
    }
  }, [api, businessId]);

  // Initial load on user change.
  useEffect(() => {
    setLoading(true);
    fetchLocations();
  }, [fetchLocations]);

  // Restore last-selected location for this business.
  useEffect(() => {
    if (!businessId) return;
    const saved = localStorage.getItem(`nb-active-location-${businessId}`);
    if (saved) _setActiveLocationId(saved);
  }, [businessId]);

  const setActiveLocationId = useCallback((id) => {
    _setActiveLocationId(id);
    if (businessId) localStorage.setItem(`nb-active-location-${businessId}`, id);
  }, [businessId]);

  const canAddLocation = locations.length < maxLocations;

  const apiQueryParam = useCallback((extra = '') => {
    if (activeLocationId === 'all') return extra ? `?${extra}` : '';
    const sep = extra ? '&' : '';
    return `?location_id=${encodeURIComponent(activeLocationId)}${sep}${extra}`;
  }, [activeLocationId]);

  const value = useMemo(() => ({
    locations,
    primaryId,
    maxLocations,
    canAddLocation,
    activeLocationId,
    setActiveLocationId,
    refresh: fetchLocations,
    apiQueryParam,
    loading,
    isMultiLocation: locations.length > 1,
  }), [locations, primaryId, maxLocations, canAddLocation, activeLocationId, setActiveLocationId, fetchLocations, apiQueryParam, loading]);

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocations() {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    // Outside the provider — return a no-op shape so consuming components
    // never crash on dashboards that haven't been wired through the
    // provider yet (we add it in DashboardLayout).
    return {
      locations: [], primaryId: null, maxLocations: 1, canAddLocation: false,
      activeLocationId: 'all', setActiveLocationId: () => {}, refresh: async () => {},
      apiQueryParam: () => '', loading: false, isMultiLocation: false,
    };
  }
  return ctx;
}
